define('drops/audioPlayer/audioPlayer',[
    "jquery",
    "underscore",
    "globalize",
    "lq",
    "lq/drop",
    "lq/style",
    "lq/util",
    "lq/multiSourceValue",
    "./audioPlayerCustom",
    "./audioPlayerNative",
], function ($, _, globalize, lq, Drop, Style, util, multiSourceValue, AudioPlayerCustom, AudioPlayerNative) {
    "use strict";

    var defaults = {
        data: {
            autoPlay: false,
            loop: false,
            muted: false,
            preload: "metadata",
            skipTimeMs: 10000,
            format: "mm:ss",
            size: { x: 720, y: 60 },
            useNativePlayer: false,
            speedControlType: "button",
            speedPostfix: "x",
            playbackRates: [1.0, 1.25, 1.5, 2.0],
            skipToTimestamp: multiSourceValue.createEmptyValue(""),
        },
        childDrops: [],
    };

    function formatTime(seconds, format) {
        if (!seconds || isNaN(seconds) || seconds < 0) seconds = 0;
        var date = new Date(1970, 0, 1, 0, 0, 0, Math.round(seconds * 1000));

        if (format) {
            try {
                return globalize.format(date, format);
            } catch (e) {
                return "Invalid format";
            }
        }

        return globalize.format(date, "HH:mm:ss.ff");
    }

    function parseTimestampSeconds(val) {
        if (val === null || val === undefined || val === "") return null;
        var num = parseFloat(val);
        return isNaN(num) || num < 0 ? null : num;
    }

    // Resolves raw URLs, relative paths, or DynamicContent references into playable audio URLs.
    function resolveAudioUrl(str) {
        if (!str || typeof str !== "string" || str.length === 0) return "";
        if (
            str.indexOf("http://") === 0 ||
            str.indexOf("https://") === 0 ||
            str.indexOf("//") === 0 ||
            str.indexOf("data:") === 0 ||
            str.indexOf("blob:") === 0 ||
            str.indexOf("/") === 0
        ) {
            return str;
        }
        if (str.indexOf("DynamicContent/File/") === 0) {
            return lq.getTenantUrl(str.replace("DynamicContent/File/", "DynamicContent/Stream/"));
        }
        if (str.indexOf("DynamicContent/") === 0) {
            return lq.getTenantUrl(str);
        }
        return lq.getTenantUrl("DynamicContent/Stream/" + str);
    }

    // Creates a delegator function that forwards calls to the active player strategy (custom or native).
    function delegateToPlayer(methodName, defaultReturn) {
        return function () {
            if (this.player && typeof this.player[methodName] === "function") {
                return this.player[methodName].apply(this.player, arguments);
            }
            return defaultReturn;
        };
    }

    function AudioPlayer(options) {
        Drop.call(this, $("<div/>"), defaults, options);

        this.$element.addClass("boxToBorderSize audio-player-container");

        this.audioUrl = null;
        this.player = null;

        return this;
    }

    Drop.extend(AudioPlayer, Drop);

    AudioPlayer.typeName = "AudioPlayer";

    AudioPlayer.prototype.shouldUseNativePlayer = function () {
        return !!(this.data && (this.data.useNativePlayer || this.data.useNative));
    };

    AudioPlayer.prototype.preRender = function () {
        this.piping.visibility.apply(this);
        this.applyPosition();
        Style.renderStyles(this);

        multiSourceValue.initializeUpdates(this, this.data.skipToTimestamp, this.onSkipToTimestampUpdated.bind(this));
    };

    AudioPlayer.prototype.getInitialSkipToTimestampSeconds = function () {
        if (!this.data || !this.data.skipToTimestamp) return 0;
        var raw = multiSourceValue.getValue(this, this.data.skipToTimestamp);
        var sec = parseTimestampSeconds(raw);
        return sec !== null ? sec : 0;
    };

    AudioPlayer.prototype.onSkipToTimestampUpdated = function () {
        var raw = multiSourceValue.getValue(this, this.data.skipToTimestamp);
        var sec = parseTimestampSeconds(raw);

        if (sec !== null && this.player && typeof this.player.seekTo === "function") {
            this.player.seekTo(sec);
        }
    };

    AudioPlayer.prototype.render = function ($element) {
        return this.createAndRenderDrops($element);
    };

    AudioPlayer.prototype.postRender = function () {
        this.player = this.shouldUseNativePlayer() ? new AudioPlayerNative(this) : new AudioPlayerCustom(this);

        this.player.initialize();
    };

    AudioPlayer.prototype.unsubscribeEventHandlers = function () {
        if (this.player && typeof this.player.dispose === "function") {
            this.player.dispose();
        }
    };

    AudioPlayer.prototype.setValue = function (update) {
        if (update == null || update === "") {
            this.clearValue();
            return;
        }

        var rawValue = update.value !== undefined ? update.value : update;
        if (rawValue == null || rawValue === "") {
            this.clearValue();
            return;
        }

        var value = util.convertListOfKeyValuesToObject(rawValue);
        var rawUrl = (value && (value.url || value.Url)) || value || rawValue;
        var audioUrl = typeof rawUrl === "string" ? resolveAudioUrl(rawUrl) : "";

        if (audioUrl) {
            this.setAudioUrl(audioUrl);
        } else {
            this.clearValue();
        }
    };

    AudioPlayer.prototype.getValue = function () {
        return this.getAudioUrl() || null;
    };

    AudioPlayer.prototype.clearValue = function () {
        this.audioUrl = null;
        if (this.player && typeof this.player.clearValue === "function") {
            this.player.clearValue();
        }
    };

    AudioPlayer.prototype.setAudioUrl = function (url) {
        this.audioUrl = url || null;
        if (this.player && typeof this.player.setAudioUrl === "function") {
            this.player.setAudioUrl(this.audioUrl);
        }
    };

    AudioPlayer.prototype.getAudioUrl = function () {
        return this.audioUrl || "";
    };

    AudioPlayer.prototype.getDefaultEventArg = function () {
        return this.getValue();
    };

    AudioPlayer.prototype.getDropRole = function (drop) {
        if (!drop || !drop.data) return "";
        return drop.data.audioRole || drop.data.role || "";
    };

    // Recursively traverses the child drop tree until callback returns true.
    AudioPlayer.prototype.traverseDrops = function (callback) {
        function traverse(drop) {
            if (!drop || !drop.pool) return false;
            for (var i = 0; i < drop.pool.length; i++) {
                var child = drop.pool[i];
                if (callback(child) === true) {
                    return true;
                }
                if (traverse(child)) {
                    return true;
                }
            }
            return false;
        }

        traverse(this);
    };

    AudioPlayer.prototype.findChildDropByRole = function (roleName) {
        if (!roleName) return null;
        var self = this;
        var found = null;
        this.traverseDrops(function (drop) {
            if (self.getDropRole(drop) === roleName) {
                found = drop;
                return true;
            }
        });
        return found;
    };

    AudioPlayer.prototype.format = function (seconds, customPattern) {
        var pattern =
            customPattern !== undefined ? customPattern : this.data && (this.data.format || this.data.timeFormat);
        return formatTime(seconds, pattern);
    };

    var playerMethods = [
        "playAudio",
        "pauseAudio",
        "togglePlayPause",
        "setMuted",
        "toggleMute",
        "skip",
        "seekTo",
        "setVolume",
        "setPlaybackRate",
        "cyclePlaybackRate",
        "resetPlayerState",
        "afterShown",
        "afterHidden",
    ];

    _.each(playerMethods, function (methodName) {
        AudioPlayer.prototype[methodName] = delegateToPlayer(methodName);
    });

    AudioPlayer.prototype.getVolume = delegateToPlayer("getVolume", 100);

    // Parses and returns configured playback rates, normalizing object/numeric items with fallback defaults.
    AudioPlayer.prototype.getPlaybackRates = function () {
        var rates = this.data && this.data.playbackRates;
        if (!rates || !rates.length) {
            return defaults.data.playbackRates;
        }
        var parsed = _.chain(rates)
            .map(function (item) {
                var val = typeof item === "object" && item !== null ? item.speed : item;
                var num = parseFloat(val);
                return isNaN(num) || num <= 0 ? null : num;
            })
            .compact()
            .value();

        return parsed.length > 0 ? parsed : defaults.data.playbackRates;
    };

    AudioPlayer.prototype.getSpeedPostfix = function () {
        if (this.data && this.data.speedPostfix !== undefined && this.data.speedPostfix !== null) {
            return this.data.speedPostfix;
        }
        return "x";
    };

    return AudioPlayer;
});

